import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicInvitationsApi } from '../../_lib/api-client';
import { InvitationPage, AttendanceStatus } from '../../_graphql/generated/types';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import InvitationPreviewV2 from '../../_components/InvitationPreview/InvitationPreviewV2';
import { createUrlSafeEventName } from '../../_utils/eventUtils';

const PublicInvitationV2: React.FC = () => {
  const { eventCode, token } = useParams<{ eventCode: string; token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [invitationPage, setInvitationPage] = useState<InvitationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasResponded, setHasResponded] = useState(false);
  const [showRSVPForm, setShowRSVPForm] = useState(false);
  const [guestResponses, setGuestResponses] = useState<{ [guestId: string]: AttendanceStatus }>({});
  const [dietaryRequirements, setDietaryRequirements] = useState<{ [guestId: string]: string }>({});
  const [guestNames, setGuestNames] = useState<{ [guestId: string]: string }>({});
  const [message, setMessage] = useState('');
  const preventRedirectRef = useRef(false);

  // Handle navigation state for Change RSVP
  useEffect(() => {
    if (location.state?.changeRSVP === true) {
      preventRedirectRef.current = true;
      setShowRSVPForm(true);
      // Clear the navigation state to prevent issues on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token || !eventCode) {
        setError(t('error.invalidLink'));
        setLoading(false);
        return;
      }

      try {
        const invitationData = await publicInvitationsApi.getByToken(token, eventCode);
        
        if (!invitationData) {
          setError(t('error.notFound'));
          setLoading(false);
          return;
        }

        // Sort guests according to the specified logic
        const guestMap = new Map(invitationData.invitation.guests.map(g => [g.id, g]));
        const sortedGuests: typeof invitationData.invitation.guests = [];
        const processedIds = new Set<string>();
        
        // First, process all primary guests in order of guest number
        const primaryGuests = invitationData.invitation.guests
          .filter(g => !g.primaryGuestId)
          .sort((a, b) => a.guestNumber - b.guestNumber);
        
        for (const primaryGuest of primaryGuests) {
          if (processedIds.has(primaryGuest.id)) continue;
          
          // Add the primary guest
          sortedGuests.push(primaryGuest);
          processedIds.add(primaryGuest.id);
          
          // Find and add all plus ones for this primary guest, sorted by guest number
          const plusOnes = invitationData.invitation.guests
            .filter(g => g.primaryGuestId === primaryGuest.id)
            .sort((a, b) => a.guestNumber - b.guestNumber);
          
          for (const plusOne of plusOnes) {
            if (!processedIds.has(plusOne.id)) {
              sortedGuests.push(plusOne);
              processedIds.add(plusOne.id);
            }
          }
        }
        
        // Add any remaining guests (shouldn't happen with proper data, but just in case)
        for (const guest of invitationData.invitation.guests) {
          if (!processedIds.has(guest.id)) {
            sortedGuests.push(guest);
          }
        }
        
        // Update the invitation data with sorted guests
        invitationData.invitation.guests = sortedGuests;
        
        setInvitationPage(invitationData);
        
        // Initialize guest responses with current attendance status
        const responses: { [guestId: string]: AttendanceStatus } = {};
        const dietary: { [guestId: string]: string } = {};
        const names: { [guestId: string]: string } = {};
        
        invitationData.invitation.guests.forEach(guest => {
          responses[guest.id] = guest.attendance;
          dietary[guest.id] = guest.dietaryRequirements || '';
          names[guest.id] = guest.name || '';
        });
        
        setGuestResponses(responses);
        setDietaryRequirements(dietary);
        setGuestNames(names);
        
        // Check if all guests have already responded
        const allGuestsResponded = invitationData.invitation.guests.every(
          guest => guest.attendance && guest.attendance !== AttendanceStatus.NO_RESPONSE
        );
        
        // Check if at least one guest is attending
        const hasAttendingGuests = invitationData.invitation.guests.some(
          guest => guest.attendance === AttendanceStatus.ATTENDING
        );
        
        if (allGuestsResponded) {
          setHasResponded(true);
          
          // Only redirect to event homepage if at least one guest is attending and not preventing redirect
          if (hasAttendingGuests && !preventRedirectRef.current) {
            const urlSafeEventName = createUrlSafeEventName(invitationData.event.eventName);
            navigate(`/event/${eventCode}/${token}/${urlSafeEventName}`);
          }
        }
      } catch (err) {
        setError(t('error.loadFailed'));
        console.error('Error fetching invitation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token, eventCode, navigate]);

  // Update document title when invitation page is loaded
  useEffect(() => {
    if (invitationPage?.event?.eventName) {
      document.title = `${invitationPage.event.eventName} - invitewithlove.com`;
    }
    
    // Reset to default title on unmount
    return () => {
      document.title = 'invitewithlove.com';
    };
  }, [invitationPage?.event?.eventName]);

  const handleSubmitResponse = async () => {
    if (!invitationPage || !token || !eventCode) return;

    // Validate that all attending plus ones have names
    const invalidPlusOne = invitationPage.invitation.guests.find(guest => {
      const isPlusOne = !!guest.primaryGuestId;
      const isAttending = guestResponses[guest.id] === AttendanceStatus.ATTENDING;
      return isPlusOne && isAttending && (!guestNames[guest.id] || guestNames[guest.id].trim() === '');
    });

    if (invalidPlusOne) {
      alert(t('rsvp.validation.namesRequired'));
      return;
    }

    try {
      const guestAttendanceUpdates = invitationPage.invitation.guests.map(guest => ({
        guestId: guest.id,
        attendance: guestResponses[guest.id],
        dietaryRequirements: dietaryRequirements[guest.id] || undefined,
        name: guestNames[guest.id] || undefined
      }));

      await publicInvitationsApi.submitResponse({
        invitationToken: token,
        eventCode: eventCode,
        guestAttendanceUpdates,
        message: message || undefined
      });

      // Check if at least one guest is attending
      const hasAttendingGuests = guestAttendanceUpdates.some(
        update => update.attendance === AttendanceStatus.ATTENDING
      );
      
      if (hasAttendingGuests) {
        // Redirect to event homepage if at least one guest is attending
        const urlSafeEventName = createUrlSafeEventName(invitationPage.event.eventName);
        navigate(`/event/${eventCode}/${token}/${urlSafeEventName}`);
      } else {
        // Stay on invitation page but mark as responded
        setHasResponded(true);
        setShowRSVPForm(false);
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert(t('error.submitFailed'));
    }
  };

  const handleGuestResponseChange = (guestId: string, attendance: AttendanceStatus) => {
    setGuestResponses(prev => ({
      ...prev,
      [guestId]: attendance
    }));
  };

  const handleDietaryChange = (guestId: string, dietary: string) => {
    setDietaryRequirements(prev => ({
      ...prev,
      [guestId]: dietary
    }));
  };

  const handleNameChange = (guestId: string, name: string) => {
    setGuestNames(prev => ({
      ...prev,
      [guestId]: name
    }));
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading.invitation')}</p>
        </div>
      </div>
    );
  }

  if (error || !invitationPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="w-12 h-12 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">{t('error.notFound')}</h2>
          <p className="mt-2 text-gray-600">{error || t('error.notFoundDescription')}</p>
        </div>
      </div>
    );
  }


  // Parse the design from designCustomizations
  let design: InvitationDesign | null = null;
  try {
    if (invitationPage.design) {
      if (typeof invitationPage.design === 'string') {
        design = JSON.parse(invitationPage.design);
      } else {
        design = invitationPage.design as InvitationDesign;
      }
    }
  } catch (err) {
    console.error('Failed to parse design customizations:', err);
  }

  // Create a mock event object for the preview
  const mockEvent = {
    id: invitationPage.event.id,
    eventName: invitationPage.event.eventName,
    eventDateTime: invitationPage.event.eventDateTime,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    invitations: [],
    invitationTypes: []
  };

  const allResponded = invitationPage.invitation.guests.every(guest => 
    guestResponses[guest.id] && guestResponses[guest.id] !== AttendanceStatus.NO_RESPONSE
  );

  // Check if all attending plus ones have names
  const allAttendingPlusOnesHaveNames = invitationPage.invitation.guests.every(guest => {
    const isPlusOne = !!guest.primaryGuestId;
    const isAttending = guestResponses[guest.id] === AttendanceStatus.ATTENDING;
    if (isPlusOne && isAttending) {
      return guestNames[guest.id] && guestNames[guest.id].trim() !== '';
    }
    return true;
  });

  const canSubmit = allResponded && allAttendingPlusOnesHaveNames;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-[528px] mx-auto">
        {/* Invitation Preview */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" id="invitation-preview">
          {design ? (
            <InvitationPreviewV2
              design={design}
              event={mockEvent}
              isEditing={false}
            />
          ) : (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg font-medium">{invitationPage.event.eventName}</p>
              <p className="mt-2">{new Date(invitationPage.event.eventDateTime).toLocaleDateString()}</p>
              <p className="mt-4 text-sm">{t('error.designNotAvailable')}</p>
            </div>
          )}
        </div>


        {/* Message for declined guests */}
        {hasResponded && !showRSVPForm && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-gray-700">
              {t('rsvp.thankYouMessage')}
            </p>
          </div>
        )}

        {/* RSVP Section */}
        {!showRSVPForm ? (
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowRSVPForm(true)}
              className="px-8 py-3 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
            >
              {hasResponded ? t('rsvp.changeButton') : t('rsvp.button')}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-4 max-h-[90vh] flex flex-col">
            {/* Header section */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">{t('rsvp.respondBy', { date: new Date(invitationPage.rsvpDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) })}</h3>
              <button
                onClick={() => setShowRSVPForm(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label={t('rsvp.closeForm')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              
              <div className="space-y-4">
              {/* Guest Responses - Compact Layout */}
              {invitationPage.invitation.guests.map((guest, index) => {
                const isPlusOne = !!guest.primaryGuestId;
                return (
                  <div key={guest.id} className={`${index !== invitationPage.invitation.guests.length - 1 ? 'border-b border-gray-200 pb-4' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {isPlusOne ? (
                          <span className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
                              +1
                            </span>
                            {guest.name || t('rsvp.guest')}
                          </span>
                        ) : (
                          guest.name
                        )}
                      </h4>
                      <div className="flex gap-2">
                      <label className={`flex items-center px-3 py-1.5 border rounded-md cursor-pointer text-sm transition-colors ${
                        guestResponses[guest.id] === AttendanceStatus.ATTENDING
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-300 hover:border-gray-400 text-gray-700'
                      }`}>
                        <input
                          type="radio"
                          name={`response-${guest.id}`}
                          value={AttendanceStatus.ATTENDING}
                          checked={guestResponses[guest.id] === AttendanceStatus.ATTENDING}
                          onChange={(e) => handleGuestResponseChange(guest.id, e.target.value as AttendanceStatus)}
                          className="mr-2 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span>{t('rsvp.attending')}</span>
                      </label>
                      <label className={`flex items-center px-3 py-1.5 border rounded-md cursor-pointer text-sm transition-colors ${
                        guestResponses[guest.id] === AttendanceStatus.NOT_ATTENDING
                          ? 'border-gray-500 bg-gray-100 text-gray-700'
                          : 'border-gray-300 hover:border-gray-400 text-gray-700'
                      }`}>
                        <input
                          type="radio"
                          name={`response-${guest.id}`}
                          value={AttendanceStatus.NOT_ATTENDING}
                          checked={guestResponses[guest.id] === AttendanceStatus.NOT_ATTENDING}
                          onChange={(e) => handleGuestResponseChange(guest.id, e.target.value as AttendanceStatus)}
                          className="mr-2 text-gray-600 focus:ring-gray-500"
                        />
                        <span>{t('rsvp.notAttending')}</span>
                      </label>
                    </div>
                  </div>

                  {/* Name Input for Plus Ones (required if attending) */}
                  {isPlusOne && guestResponses[guest.id] === AttendanceStatus.ATTENDING && (
                    <div className="mt-2">
                      <input
                        id={`name-${guest.id}`}
                        type="text"
                        value={guestNames[guest.id] || ''}
                        onChange={(e) => handleNameChange(guest.id, e.target.value)}
                        placeholder={t('rsvp.guestNamePlaceholder')}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                  )}

                  {/* Dietary Requirements (only show if attending) */}
                  {guestResponses[guest.id] === AttendanceStatus.ATTENDING && (
                    <div className="mt-2">
                      <input
                        id={`dietary-${guest.id}`}
                        type="text"
                        value={dietaryRequirements[guest.id] || ''}
                        onChange={(e) => handleDietaryChange(guest.id, e.target.value)}
                        placeholder={t('rsvp.dietaryPlaceholder')}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  )}
                </div>
                );
              })}

              {/* Additional Message - More Compact */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('rsvp.messageLabel')}
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onFocus={(e) => {
                    // On mobile, scroll the textarea into view when focused
                    setTimeout(() => {
                      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 300);
                  }}
                  rows={2}
                  placeholder={t('rsvp.messagePlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
              </div>

              {/* Plus Ones Info */}
              {invitationPage.invitation.allowedPlusOnes > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
                  <p className="text-xs text-indigo-700">
                    {t('rsvp.plusOneInfo', { count: invitationPage.invitation.allowedPlusOnes })}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end pt-2 pb-4 sticky bottom-0 bg-white">
                <button
                  onClick={handleSubmitResponse}
                  disabled={!canSubmit}
                  className={`px-6 py-2 rounded-md font-medium text-white transition-all text-sm focus:ring-2 focus:ring-offset-2 ${
                    canSubmit 
                      ? 'bg-gray-800 hover:bg-gray-900 focus:ring-gray-500' 
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {!allResponded ? t('rsvp.pleaseRespond') : 
                   !allAttendingPlusOnesHaveNames ? t('rsvp.pleaseEnterNames') : 
                   t('rsvp.submitButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

    </div>
  );
};

export default PublicInvitationV2;